import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { JOURNEY_ROLE } from "../../enums/scan";
import { uniqueJourneyRoutes } from "./journeyRouteIdentity";
import type {
  CandidateJourney,
  JourneyPage,
  PriorityFlowConcern,
} from "./journeyTypes";
import { buildPriorityFlowConcerns } from "./priorityFlowConcerns";
import { flowGrouping } from "./priorityFlowLabels";
import { routeClusterKey } from "./scanModel";

export type PriorityFlowGroup = {
  keys: string[];
  paths: CandidateJourney[];
  concerns: PriorityFlowConcern[];
};

export function buildMergedFlowGroups(
  data: OverviewResponse,
  paths: CandidateJourney[],
): PriorityFlowGroup[] {
  const preliminary = new Map<string, CandidateJourney[]>();
  for (const path of uniqueJourneyRoutes(paths)) {
    const key = flowGrouping(path).key;
    preliminary.set(key, [...(preliminary.get(key) ?? []), path]);
  }
  const groups = [...preliminary.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, groupedPaths]) => buildGroup(data, [key], groupedPaths));

  let merged = groups;
  let changed = true;
  while (changed) {
    changed = false;
    const next: PriorityFlowGroup[] = [];
    const consumed = new Set<number>();
    for (let leftIndex = 0; leftIndex < merged.length; leftIndex += 1) {
      if (consumed.has(leftIndex)) continue;
      let current = merged[leftIndex] as PriorityFlowGroup;
      for (
        let rightIndex = leftIndex + 1;
        rightIndex < merged.length;
        rightIndex += 1
      ) {
        if (
          consumed.has(rightIndex) ||
          !flowGroupsEquivalent(
            current,
            merged[rightIndex] as PriorityFlowGroup,
          )
        ) {
          continue;
        }
        current = buildGroup(
          data,
          [...current.keys, ...(merged[rightIndex]?.keys ?? [])],
          [...current.paths, ...(merged[rightIndex]?.paths ?? [])],
        );
        consumed.add(rightIndex);
        changed = true;
      }
      next.push(current);
    }
    merged = next;
  }
  return merged.sort(
    (left, right) =>
      minimumRank(left.paths) - minimumRank(right.paths) ||
      left.keys.join(":").localeCompare(right.keys.join(":")),
  );
}

function canonicalNonEntryPageIds(paths: CandidateJourney[]): Set<string> {
  return new Set(
    paths.flatMap((path) =>
      path.steps.flatMap((step) =>
        step.role === JOURNEY_ROLE.ENTRY ? [] : [step.page.id],
      ),
    ),
  );
}

function canonicalConcernKeys(concerns: PriorityFlowConcern[]): Set<string> {
  return new Set(concerns.map((concern) => concern.key));
}

function setOverlap(left: Set<string>, right: Set<string>): number {
  const union = new Set([...left, ...right]);
  if (union.size === 0) return 1;
  let intersection = 0;
  for (const value of left) {
    if (right.has(value)) intersection += 1;
  }
  return intersection / union.size;
}

function flowGroupsEquivalent(
  left: PriorityFlowGroup,
  right: PriorityFlowGroup,
): boolean {
  const leftPages = canonicalNonEntryPageIds(left.paths);
  const rightPages = canonicalNonEntryPageIds(right.paths);
  if (sameSet(leftPages, rightPages)) return true;
  if (setOverlap(leftPages, rightPages) < 0.85) return false;
  if (
    setOverlap(
      canonicalConcernKeys(left.concerns),
      canonicalConcernKeys(right.concerns),
    ) < 0.85
  ) {
    return false;
  }
  return (
    terminalFamiliesOverlap(left.paths, right.paths) &&
    !hasDistinctBranch(left.paths, right.paths) &&
    !hasDistinctBranch(right.paths, left.paths)
  );
}

function buildGroup(
  data: OverviewResponse,
  keys: string[],
  paths: CandidateJourney[],
): PriorityFlowGroup {
  const uniquePaths = uniqueJourneyRoutes(paths).sort(
    (left, right) => left.rank - right.rank || left.id.localeCompare(right.id),
  );
  return {
    keys: [...new Set(keys)].sort(),
    paths: uniquePaths,
    concerns: buildPriorityFlowConcerns(data, uniquePaths),
  };
}

function terminalFamiliesOverlap(
  left: CandidateJourney[],
  right: CandidateJourney[],
): boolean {
  const leftFamilies = new Set(left.map(terminalFamily));
  return right.some((path) => leftFamilies.has(terminalFamily(path)));
}

function terminalFamily(path: CandidateJourney): string {
  const terminal = path.steps.at(-1);
  if (!terminal) return "none";
  return `${terminal.role}:${routeClusterKey(terminal.page.path)}`;
}

function hasDistinctBranch(
  source: CandidateJourney[],
  comparison: CandidateJourney[],
): boolean {
  const comparisonPages = canonicalNonEntryPageIds(comparison);
  const comparisonClusters = clustersForPaths(comparison);
  return pagesForPaths(source).some(
    (page) =>
      !comparisonPages.has(page.id) &&
      !comparisonClusters.has(routeClusterKey(page.path)),
  );
}

function pagesForPaths(paths: CandidateJourney[]): JourneyPage[] {
  return [
    ...new Map(
      paths.flatMap((path) =>
        path.steps.flatMap((step) =>
          step.role === JOURNEY_ROLE.ENTRY
            ? []
            : [[step.page.id, step.page] as const],
        ),
      ),
    ).values(),
  ];
}

function clustersForPaths(paths: CandidateJourney[]): Set<string> {
  return new Set(
    pagesForPaths(paths).map((page) => routeClusterKey(page.path)),
  );
}

function sameSet(left: Set<string>, right: Set<string>): boolean {
  return (
    left.size === right.size && [...left].every((value) => right.has(value))
  );
}

function minimumRank(paths: CandidateJourney[]): number {
  return Math.min(...paths.map((path) => path.rank));
}
